import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMember } from '../context/MemberContext';
import { 
    Shield, Users, FileText, Check, X, Trash2, Eye, Plus, Ship, Megaphone, Target, 
    Key, Edit, Save, BarChart3, Crosshair, Image, Layers, Radio, Award, UserPlus, Lock, ChevronUp, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Admin = () => {
    const { admin, loading, getAuthHeaders } = useAuth();
    const { member } = useMember();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('applications');
    const [applications, setApplications] = useState([]);
    const [members, setMembers] = useState([]);
    const [vessels, setVessels] = useState([]);
    const [squadrons, setSquadrons] = useState([]);
    const [operations, setOperations] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [shipClasses, setShipClasses] = useState([]);
    const [ranks, setRanks] = useState([]);
    const [tickerMessages, setTickerMessages] = useState([]);
    const [tickerSpeed, setTickerSpeed] = useState(30);
    const [stats, setStats] = useState({ active_pilots: 126, fleet_vessels: 47, capital_ships: 12, active_operations: 3 });
    const [backgrounds, setBackgrounds] = useState({ bridge: '', hangar: '', war_room: '', personnel: '', operations: '', announcements: '' });
    const [selectedApp, setSelectedApp] = useState(null);
    const [orgMembers, setOrgMembers] = useState([]);
    
    const isFounder = admin?.username === 'AceShepherd';
    
    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [modalType, setModalType] = useState('');
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({});

    useEffect(() => {
        if (!loading && !admin) {
            navigate(member ? '/operations' : '/login');
        }
    }, [admin, loading, navigate, member]);

    useEffect(() => {
        if (admin) {
            fetchAllData();
        }
    }, [admin]);

    const fetchAllData = async () => {
        try {
            const [appsRes, membersRes, vesselsRes, squadronsRes, opsRes, annsRes, adminsRes, statsRes, bgRes, classesRes, ranksRes, tickerRes, tickerSettingsRes, orgMembersRes] = await Promise.allSettled([
                axios.get(`${API}/applications`, getAuthHeaders()),
                axios.get(`${API}/members`),
                axios.get(`${API}/vessels/all`, getAuthHeaders()),
                axios.get(`${API}/squadrons`),
                axios.get(`${API}/operations/all`, getAuthHeaders()),
                axios.get(`${API}/announcements`),
                axios.get(`${API}/admins`, getAuthHeaders()),
                axios.get(`${API}/stats`),
                axios.get(`${API}/backgrounds`),
                axios.get(`${API}/ship-classes`),
                axios.get(`${API}/ranks`),
                axios.get(`${API}/ticker-messages`),
                axios.get(`${API}/ticker-settings`),
                axios.get(`${API}/org-members`, getAuthHeaders())
            ]);
            if (appsRes.status === 'fulfilled') setApplications(appsRes.value.data);
            if (membersRes.status === 'fulfilled') setMembers(membersRes.value.data);
            if (vesselsRes.status === 'fulfilled') setVessels(vesselsRes.value.data);
            if (squadronsRes.status === 'fulfilled') setSquadrons(squadronsRes.value.data);
            if (opsRes.status === 'fulfilled') setOperations(opsRes.value.data);
            if (annsRes.status === 'fulfilled') setAnnouncements(annsRes.value.data);
            if (adminsRes.status === 'fulfilled') setAdmins(adminsRes.value.data);
            if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
            if (bgRes.status === 'fulfilled') setBackgrounds(bgRes.value.data);
            if (classesRes.status === 'fulfilled') setShipClasses(classesRes.value.data);
            if (ranksRes.status === 'fulfilled') setRanks(ranksRes.value.data);
            if (tickerRes.status === 'fulfilled') setTickerMessages(tickerRes.value.data);
            if (tickerSettingsRes.status === 'fulfilled') setTickerSpeed(tickerSettingsRes.value.data.speed || 30);
            if (orgMembersRes.status === 'fulfilled') setOrgMembers(orgMembersRes.value.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        }
    };

    const updateApplicationStatus = async (appId, status) => {
        try {
            await axios.patch(`${API}/applications/${appId}/status?status=${status}`, {}, getAuthHeaders());
            toast.success(`Application ${status}`);
            fetchAllData();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const setVesselApproval = async (vesselId, approvalStatus) => {
        try {
            await axios.patch(`${API}/vessels/${vesselId}/approval?approval_status=${approvalStatus}`, {}, getAuthHeaders());
            toast.success(approvalStatus === 'approved' ? 'Vessel approved' : 'Vessel rejected');
            fetchAllData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update vessel');
        }
    };

    const linkOrgMemberAdmin = async (memberId, adminId) => {
        try {
            await axios.patch(`${API}/org-members/${memberId}/link-admin`, { admin_id: adminId || null }, getAuthHeaders());
            toast.success(adminId ? 'Account linked to admin' : 'Account unlinked');
            fetchAllData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to link account');
        }
    };


    const moveMember = async (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= members.length) return;
        const reordered = [...members];
        [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
        setMembers(reordered);
        try {
            await axios.put(`${API}/members/reorder`, { ids: reordered.map(m => m.id) }, getAuthHeaders());
        } catch (error) {
            toast.error('Failed to reorder members');
            fetchAllData();
        }
    };


    const approveOrgMember = async (memberId) => {
        try {
            await axios.patch(`${API}/org-members/${memberId}/approve`, {}, getAuthHeaders());
            toast.success('Member approved');
            fetchAllData();
        } catch (error) {
            toast.error('Failed to approve member');
        }
    };

    const rejectOrgMember = async (memberId) => {
        try {
            await axios.patch(`${API}/org-members/${memberId}/reject`, {}, getAuthHeaders());
            toast.success('Member rejected');
            fetchAllData();
        } catch (error) {
            toast.error('Failed to reject member');
        }
    };

    const deleteOrgMember = async (memberId) => {
        try {
            await axios.delete(`${API}/org-members/${memberId}`, getAuthHeaders());
            toast.success('Member deleted');
            fetchAllData();
        } catch (error) {
            toast.error('Failed to delete member');
        }
    };

    const deleteItem = async (type, id) => {
        try {
            await axios.delete(`${API}/${type}/${id}`, getAuthHeaders());
            toast.success('Item deleted');
            fetchAllData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete');
        }
    };

    const openAddModal = (type) => {
        setModalType(type);
        setFormData(getDefaultFormData(type));
        setShowAddModal(true);
    };

    const openEditModal = (type, item) => {
        setModalType(type);
        setEditItem(item);
        setFormData(item);
        setShowEditModal(true);
    };

    const getDefaultFormData = (type) => {
        switch (type) {
            case 'vessel':
                return { name: '', ship_class: shipClasses[0]?.name || 'Carrier', role: '', manufacturer: '', crew_size: '', status: 'active', squadron: '', visibility: 'public' };
            case 'squadron':
                return { name: '', designation: '', role: '', commander: '', ship_count: 0 };
            case 'operation':
                return { name: '', code_name: '', status: 'planned', system: 'STANTON', objective: '', start_date: '', end_date: '', participants: [], visibility: 'public' };
            case 'announcement':
                return { title: '', content: '', author: admin?.username || '', priority: 'normal' };
            case 'admin':
                return { username: '', password: '' };
            case 'member':
                return { callsign: '', rank: ranks[ranks.length - 1]?.name || 'Ensign', division: '', role: '', join_date: new Date().toISOString().split('T')[0], status: 'active' };
            case 'shipclass':
                return { name: '', description: '', vessel_type: 'fleet' };
            case 'rank':
                return { name: '', hierarchy: ranks.length + 1, color: '#A3A3A3' };
            case 'ticker':
                return { message: '', active: true };
            default:
                return {};
        }
    };

    const handleSubmitAdd = async () => {
        try {
            let endpoint = '';
            let data = { ...formData };
            
            switch (modalType) {
                case 'vessel':
                    endpoint = '/vessels';
                    break;
                case 'squadron':
                    endpoint = '/squadrons';
                    data.ship_count = parseInt(data.ship_count) || 0;
                    break;
                case 'operation':
                    endpoint = '/operations';
                    if (typeof data.participants === 'string') {
                        data.participants = data.participants.split(',').map(p => p.trim()).filter(p => p);
                    }
                    break;
                case 'announcement':
                    endpoint = '/announcements';
                    break;
                case 'admin':
                    endpoint = '/admins';
                    break;
                case 'member':
                    endpoint = '/members';
                    break;
                case 'shipclass':
                    endpoint = '/ship-classes';
                    break;
                case 'rank':
                    endpoint = '/ranks';
                    data.hierarchy = parseInt(data.hierarchy) || 0;
                    break;
                case 'ticker':
                    endpoint = '/ticker-messages';
                    break;
                default:
                    return;
            }
            
            await axios.post(`${API}${endpoint}`, data, getAuthHeaders());
            toast.success(`${modalType} added successfully`);
            setShowAddModal(false);
            fetchAllData();
        } catch (error) {
            toast.error(error.response?.data?.detail || `Failed to add ${modalType}`);
        }
    };

    const handleSubmitEdit = async () => {
        try {
            let endpoint = '';
            let data = { ...formData };
            
            switch (modalType) {
                case 'vessel':
                    endpoint = `/vessels/${editItem.id}`;
                    break;
                case 'squadron':
                    endpoint = `/squadrons/${editItem.id}`;
                    data.ship_count = parseInt(data.ship_count) || 0;
                    break;
                case 'operation':
                    endpoint = `/operations/${editItem.id}`;
                    if (typeof data.participants === 'string') {
                        data.participants = data.participants.split(',').map(p => p.trim()).filter(p => p);
                    }
                    break;
                case 'announcement':
                    endpoint = `/announcements/${editItem.id}`;
                    break;
                case 'member':
                    endpoint = `/members/${editItem.id}`;
                    break;
                case 'shipclass':
                    endpoint = `/ship-classes/${editItem.id}`;
                    break;
                case 'rank':
                    endpoint = `/ranks/${editItem.id}`;
                    data.hierarchy = parseInt(data.hierarchy) || 0;
                    break;
                case 'ticker':
                    endpoint = `/ticker-messages/${editItem.id}`;
                    break;
                default:
                    return;
            }
            
            await axios.patch(`${API}${endpoint}`, data, getAuthHeaders());
            toast.success(`${modalType} updated successfully`);
            setShowEditModal(false);
            fetchAllData();
        } catch (error) {
            toast.error(error.response?.data?.detail || `Failed to update ${modalType}`);
        }
    };

    const handleUpdateStats = async () => {
        try {
            await axios.put(`${API}/stats`, stats, getAuthHeaders());
            toast.success('Stats updated successfully');
        } catch (error) {
            toast.error('Failed to update stats');
        }
    };

    const handleUpdateBackgrounds = async () => {
        try {
            await axios.put(`${API}/backgrounds`, backgrounds, getAuthHeaders());
            toast.success('Page backgrounds updated successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update backgrounds');
        }
    };

    const handleUpdateTickerSpeed = async (newSpeed) => {
        setTickerSpeed(newSpeed);
        try {
            await axios.put(`${API}/ticker-settings`, { speed: newSpeed }, getAuthHeaders());
            toast.success('Ticker speed updated');
        } catch (error) {
            toast.error('Failed to update ticker speed');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] pt-16 flex items-center justify-center">
                <div className="font-mono text-gray-500">Loading...</div>
            </div>
        );
    }

    if (!admin) return null;

    const pendingApps = applications.filter(a => a.status === 'pending');
    const pendingVessels = vessels.filter(v => v.approval_status === 'pending');
    const registryVessels = vessels.filter(v => !['pending', 'rejected'].includes(v.approval_status));

    const renderFormFields = () => {
        switch (modalType) {
            case 'vessel':
                return (
                    <>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Vessel Name</label>
                                <input className="form-input" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} />
                            </div>
                            <div>
                                <label className="form-label">Class</label>
                                <select className="form-input" data-testid="vessel-class-select" value={formData.ship_class || ''} onChange={e => setFormData({...formData, ship_class: e.target.value})}>
                                    <option value="" disabled>Select class</option>
                                    {formData.ship_class && !shipClasses.some(sc => sc.name === formData.ship_class) && (
                                        <option value={formData.ship_class}>{formData.ship_class}</option>
                                    )}
                                    {shipClasses.map((sc) => (
                                        <option key={sc.id} value={sc.name}>{sc.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Role</label>
                                <input className="form-input" value={formData.role || ''} onChange={e => setFormData({...formData, role: e.target.value})} />
                            </div>
                            <div>
                                <label className="form-label">Manufacturer</label>
                                <input className="form-input" value={formData.manufacturer || ''} onChange={e => setFormData({...formData, manufacturer: e.target.value})} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Crew Size</label>
                                <input className="form-input" value={formData.crew_size || ''} onChange={e => setFormData({...formData, crew_size: e.target.value})} />
                            </div>
                            <div>
                                <label className="form-label">Squadron</label>
                                <input className="form-input" value={formData.squadron || ''} onChange={e => setFormData({...formData, squadron: e.target.value})} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Visibility</label>
                                <select className="form-input" value={formData.visibility || 'public'} onChange={e => setFormData({...formData, visibility: e.target.value})}>
                                    <option value="public">Unclassified - Public in Hangar</option>
                                    <option value="private">Classified - Members only</option>
                                </select>
                            </div>
                            <div>
                                <label className="form-label">Status</label>
                                <select className="form-input" value={formData.status || 'active'} onChange={e => setFormData({...formData, status: e.target.value})}>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="maintenance">Maintenance</option>
                                </select>
                            </div>
                        </div>
                    </>
                );
            case 'squadron':
                return (
                    <>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Squadron Name</label>
                                <input className="form-input" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} />
                            </div>
                            <div>
                                <label className="form-label">Designation</label>
                                <input className="form-input" value={formData.designation || ''} onChange={e => setFormData({...formData, designation: e.target.value})} placeholder="e.g., ALPHA-01" />
                            </div>
                        </div>
                        <div>
                            <label className="form-label">Role</label>
                            <input className="form-input" value={formData.role || ''} onChange={e => setFormData({...formData, role: e.target.value})} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Commander</label>
                                <input className="form-input" value={formData.commander || ''} onChange={e => setFormData({...formData, commander: e.target.value})} />
                            </div>
                            <div>
                                <label className="form-label">Ship Count</label>
                                <input className="form-input" type="number" value={formData.ship_count || 0} onChange={e => setFormData({...formData, ship_count: e.target.value})} />
                            </div>
                        </div>
                    </>
                );
            case 'operation':
                return (
                    <>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Operation Name</label>
                                <input className="form-input" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} />
                            </div>
                            <div>
                                <label className="form-label">Code Name</label>
                                <input className="form-input" value={formData.code_name || ''} onChange={e => setFormData({...formData, code_name: e.target.value})} placeholder="e.g., OP CRIMSON FANG" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Status</label>
                                <select className="form-input" value={formData.status || 'planned'} onChange={e => setFormData({...formData, status: e.target.value})}>
                                    <option value="planned">Planned</option>
                                    <option value="active">Active</option>
                                    <option value="completed">Completed</option>
                                </select>
                            </div>
                            <div>
                                <label className="form-label">System</label>
                                <input className="form-input" value={formData.system || ''} onChange={e => setFormData({...formData, system: e.target.value})} placeholder="e.g., STANTON" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Visibility</label>
                                <select className="form-input" value={formData.visibility || 'public'} onChange={e => setFormData({...formData, visibility: e.target.value})}>
                                    <option value="public">Public - Visible to all</option>
                                    <option value="private">Private - Members only</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="form-label">Objective</label>
                            <textarea className="form-input" rows={2} value={formData.objective || ''} onChange={e => setFormData({...formData, objective: e.target.value})} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Start Date</label>
                                <input className="form-input" type="date" value={formData.start_date || ''} onChange={e => setFormData({...formData, start_date: e.target.value})} />
                            </div>
                            <div>
                                <label className="form-label">End Date</label>
                                <input className="form-input" type="date" value={formData.end_date || ''} onChange={e => setFormData({...formData, end_date: e.target.value})} />
                            </div>
                        </div>
                        <div>
                            <label className="form-label">Participants (comma-separated)</label>
                            <input className="form-input" value={Array.isArray(formData.participants) ? formData.participants.join(', ') : formData.participants || ''} onChange={e => setFormData({...formData, participants: e.target.value})} placeholder="Alpha Strike, Bravo Heavy" />
                        </div>
                    </>
                );
            case 'announcement':
                return (
                    <>
                        <div>
                            <label className="form-label">Title</label>
                            <input className="form-input" value={formData.title || ''} onChange={e => setFormData({...formData, title: e.target.value})} />
                        </div>
                        <div>
                            <label className="form-label">Content</label>
                            <textarea className="form-input" rows={4} value={formData.content || ''} onChange={e => setFormData({...formData, content: e.target.value})} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Author</label>
                                <input className="form-input" value={formData.author || ''} onChange={e => setFormData({...formData, author: e.target.value})} />
                            </div>
                            <div>
                                <label className="form-label">Priority</label>
                                <select className="form-input" value={formData.priority || 'normal'} onChange={e => setFormData({...formData, priority: e.target.value})}>
                                    <option value="low">Low</option>
                                    <option value="normal">Normal</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                            </div>
                        </div>
                    </>
                );
            case 'admin':
                return (
                    <>
                        <div>
                            <label className="form-label">Username</label>
                            <input className="form-input" value={formData.username || ''} onChange={e => setFormData({...formData, username: e.target.value})} />
                        </div>
                        <div>
                            <label className="form-label">Password</label>
                            <input className="form-input" type="password" value={formData.password || ''} onChange={e => setFormData({...formData, password: e.target.value})} />
                        </div>
                    </>
                );
            case 'member':
                return (
                    <>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Callsign</label>
                                <input className="form-input" value={formData.callsign || ''} onChange={e => setFormData({...formData, callsign: e.target.value})} />
                            </div>
                            <div>
                                <label className="form-label">Rank</label>
                                <select className="form-input" value={formData.rank || ''} onChange={e => setFormData({...formData, rank: e.target.value})}>
                                    {ranks.map((r) => (
                                        <option key={r.id} value={r.name}>{r.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Division</label>
                                <input className="form-input" value={formData.division || ''} onChange={e => setFormData({...formData, division: e.target.value})} placeholder="e.g., Strike, Command" />
                            </div>
                            <div>
                                <label className="form-label">Role</label>
                                <input className="form-input" value={formData.role || ''} onChange={e => setFormData({...formData, role: e.target.value})} placeholder="e.g., Pilot, Commander" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Join Date</label>
                                <input className="form-input" type="date" value={formData.join_date || ''} onChange={e => setFormData({...formData, join_date: e.target.value})} />
                            </div>
                            <div>
                                <label className="form-label">Status</label>
                                <select className="form-input" value={formData.status || 'active'} onChange={e => setFormData({...formData, status: e.target.value})}>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="on_leave">On Leave</option>
                                </select>
                            </div>
                        </div>
                    </>
                );
            case 'shipclass':
                return (
                    <>
                        <div>
                            <label className="form-label">Class Name</label>
                            <input className="form-input" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g., Battlecruiser" />
                        </div>
                        <div>
                            <label className="form-label">Vessel Type</label>
                            <select className="form-input" value={formData.vessel_type || 'fleet'} onChange={e => setFormData({...formData, vessel_type: e.target.value})}>
                                <option value="command">Command Vessel</option>
                                <option value="fleet">Fleet Vessel</option>
                            </select>
                            <p className="font-mono text-[10px] text-gray-500 mt-1">Determines which Hangar tab this class appears in</p>
                        </div>
                        <div>
                            <label className="form-label">Description</label>
                            <textarea className="form-input" rows={2} value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="Brief description of this ship class" />
                        </div>
                    </>
                );
            case 'rank':
                return (
                    <>
                        <div>
                            <label className="form-label">Rank Name</label>
                            <input className="form-input" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g., Fleet Admiral" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Hierarchy (1 = Highest)</label>
                                <input className="form-input" type="number" min="1" value={formData.hierarchy || 1} onChange={e => setFormData({...formData, hierarchy: e.target.value})} />
                                <p className="font-mono text-[10px] text-gray-500 mt-1">Lower number = higher rank</p>
                            </div>
                            <div>
                                <label className="form-label">Color</label>
                                <div className="flex gap-2">
                                    <input 
                                        type="color" 
                                        value={formData.color || '#A3A3A3'} 
                                        onChange={e => setFormData({...formData, color: e.target.value})}
                                        className="w-12 h-10 bg-transparent border border-white/10 cursor-pointer"
                                    />
                                    <input 
                                        className="form-input flex-1" 
                                        value={formData.color || '#A3A3A3'} 
                                        onChange={e => setFormData({...formData, color: e.target.value})}
                                        placeholder="#F59E0B"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="tactical-panel p-3 mt-2">
                            <span className="font-mono text-xs text-gray-500">Preview: </span>
                            <span className="font-mono text-sm font-bold" style={{ color: formData.color || '#A3A3A3' }}>
                                {formData.name || 'Rank Name'}
                            </span>
                        </div>
                    </>
                );
            case 'ticker':
                return (
                    <>
                        <div>
                            <label className="form-label">Message</label>
                            <textarea className="form-input" rows={3} value={formData.message || ''} onChange={e => setFormData({...formData, message: e.target.value})} placeholder='e.g., COMMS RELAY — STANTON — "Fleet operations commencing at 0800 UTC"' />
                        </div>
                        <div className="flex items-center gap-3">
                            <input 
                                type="checkbox" 
                                id="ticker-active"
                                checked={formData.active !== false} 
                                onChange={e => setFormData({...formData, active: e.target.checked})}
                                className="w-4 h-4 accent-red-500"
                            />
                            <label htmlFor="ticker-active" className="form-label mb-0">Active (show in ticker)</label>
                        </div>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] pt-16" data-testid="admin-page">
            <Toaster position="top-right" theme="dark" />
            
            {/* Header */}
            <section className="px-4 py-8 border-b border-white/10">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center gap-3 mb-2">
                        <Shield className="w-6 h-6 text-red-500" />
                        <span className="font-mono text-xs text-red-500 tracking-[0.3em]">COMMAND ACCESS</span>
                    </div>
                    <h1 className="font-heading font-black text-3xl text-white uppercase">
                        Admin Dashboard
                    </h1>
                    <p className="font-mono text-sm text-gray-400 mt-2">
                        Welcome, {admin.username} • Full fleet management access
                    </p>
                </div>
            </section>

            {/* Stats Editor */}
            <section className="px-4 py-6 border-b border-white/10">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center gap-3 mb-4">
                        <BarChart3 className="w-5 h-5 text-red-500" />
                        <h2 className="font-heading font-bold text-lg text-white uppercase">Fleet Statistics</h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="tactical-panel p-4">
                            <label className="form-label">Active Pilots</label>
                            <input 
                                type="number" 
                                className="form-input text-center text-xl font-mono" 
                                value={stats.active_pilots} 
                                onChange={e => setStats({...stats, active_pilots: parseInt(e.target.value) || 0})}
                            />
                        </div>
                        <div className="tactical-panel p-4">
                            <label className="form-label">Fleet Vessels</label>
                            <input 
                                type="number" 
                                className="form-input text-center text-xl font-mono" 
                                value={stats.fleet_vessels} 
                                onChange={e => setStats({...stats, fleet_vessels: parseInt(e.target.value) || 0})}
                            />
                        </div>
                        <div className="tactical-panel p-4">
                            <label className="form-label">Capital Ships</label>
                            <input 
                                type="number" 
                                className="form-input text-center text-xl font-mono" 
                                value={stats.capital_ships} 
                                onChange={e => setStats({...stats, capital_ships: parseInt(e.target.value) || 0})}
                            />
                        </div>
                        <div className="tactical-panel p-4">
                            <label className="form-label">Active Ops</label>
                            <input 
                                type="number" 
                                className="form-input text-center text-xl font-mono" 
                                value={stats.active_operations} 
                                onChange={e => setStats({...stats, active_operations: parseInt(e.target.value) || 0})}
                            />
                        </div>
                        <div className="tactical-panel p-4 flex items-end">
                            <button onClick={handleUpdateStats} className="btn-primary w-full py-3 flex items-center justify-center gap-2">
                                <Save className="w-4 h-4" /> Save Stats
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Tabs */}
            <section className="px-4 border-b border-white/10 overflow-x-auto">
                <div className="max-w-7xl mx-auto">
                    <div className="flex gap-2">
                        {[
                            { id: 'applications', label: 'Applications', icon: FileText, count: pendingApps.length },
                            { id: 'orgmembers', label: 'Org Members', icon: UserPlus, count: orgMembers.filter(m => m.status === 'pending').length },
                            { id: 'members', label: 'Members', icon: Users },
                            { id: 'ranks', label: 'Ranks', icon: Award },
                            { id: 'vessels', label: 'Vessels', icon: Ship, count: pendingVessels.length },
                            { id: 'shipclasses', label: 'Ship Classes', icon: Layers },
                            { id: 'squadrons', label: 'Squadrons', icon: Crosshair },
                            { id: 'operations', label: 'Operations', icon: Target },
                            { id: 'announcements', label: 'Announcements', icon: Megaphone },
                            { id: 'ticker', label: 'Comms Relay', icon: Radio },
                            { id: 'admins', label: 'Admins', icon: Key },
                            ...(isFounder ? [{ id: 'backgrounds', label: 'Backgrounds', icon: Image }] : []),
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 py-4 px-3 font-mono text-xs uppercase tracking-wider whitespace-nowrap transition-colors ${
                                    activeTab === tab.id 
                                        ? 'text-red-500 border-b-2 border-red-500' 
                                        : 'text-gray-500 hover:text-gray-300'
                                }`}
                                data-testid={`admin-tab-${tab.id}`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                                {tab.count > 0 && (
                                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="px-4 py-8">
                <div className="max-w-7xl mx-auto">
                    
                    {/* Applications Tab */}
                    {activeTab === 'applications' && (
                        <div data-testid="applications-tab-content">
                            {applications.length === 0 ? (
                                <div className="text-center py-12">
                                    <FileText className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                    <p className="font-mono text-gray-500">No applications yet</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {applications.map((app) => (
                                        <div key={app.id} className="tactical-panel p-4" data-testid={`application-${app.id}`}>
                                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="font-heading font-bold text-lg text-white">{app.callsign}</h3>
                                                        <span className={`badge ${app.status === 'pending' ? 'badge-pending' : app.status === 'approved' ? 'badge-active' : 'badge-urgent'}`}>
                                                            {app.status}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                                                        <span>{app.email}</span>
                                                        <span>{app.discord_id}</span>
                                                        <span>{app.preferred_role}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => setSelectedApp(selectedApp === app.id ? null : app.id)} className="btn-secondary py-2 px-3">
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    {app.status === 'pending' && (
                                                        <>
                                                            <button onClick={() => updateApplicationStatus(app.id, 'approved')} className="bg-green-600 hover:bg-green-500 text-white py-2 px-3">
                                                                <Check className="w-4 h-4" />
                                                            </button>
                                                            <button onClick={() => updateApplicationStatus(app.id, 'rejected')} className="bg-red-600 hover:bg-red-500 text-white py-2 px-3">
                                                                <X className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    )}
                                                    <button onClick={() => window.confirm(`Delete application from ${app.handle || app.name}?`) && deleteItem('applications', app.id)} className="text-red-500 hover:text-red-400 border border-red-500/30 hover:border-red-500 py-2 px-3" data-testid={`delete-application-${app.id}`}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                            {selectedApp === app.id && (
                                                <div className="mt-4 pt-4 border-t border-white/10 grid md:grid-cols-2 gap-4 text-sm">
                                                    <div><span className="text-gray-500">Experience:</span> <span className="text-gray-300">{app.experience}</span></div>
                                                    <div><span className="text-gray-500">Why Join:</span> <span className="text-gray-300">{app.why_join}</span></div>
                                                    <div><span className="text-gray-500">Play Hours:</span> <span className="text-gray-300">{app.play_hours}</span></div>
                                                    <div><span className="text-gray-500">Referred By:</span> <span className="text-gray-300">{app.referred_by || 'N/A'}</span></div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Org Members Tab */}
                    {activeTab === 'orgmembers' && (
                        <div data-testid="orgmembers-tab-content">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h3 className="font-heading font-bold text-lg text-white">Organization Member Accounts</h3>
                                    <p className="font-mono text-xs text-gray-500 mt-1">Manage member registrations and access</p>
                                </div>
                            </div>
                            
                            {/* Pending Members */}
                            {orgMembers.filter(m => m.status === 'pending').length > 0 && (
                                <div className="mb-8">
                                    <h4 className="font-mono text-sm text-amber-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                                        <Lock className="w-4 h-4" /> Pending Approval ({orgMembers.filter(m => m.status === 'pending').length})
                                    </h4>
                                    <div className="space-y-4">
                                        {orgMembers.filter(m => m.status === 'pending').map((member) => (
                                            <div key={member.id} className="tactical-panel p-4 border-l-4 border-amber-500/50" data-testid={`orgmember-${member.id}`}>
                                                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <h3 className="font-heading font-bold text-lg text-white">{member.callsign}</h3>
                                                            <span className="badge badge-pending">Pending</span>
                                                        </div>
                                                        <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                                                            <span>{member.email}</span>
                                                            <span>{member.discord_id}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button 
                                                            onClick={() => approveOrgMember(member.id)} 
                                                            className="bg-green-600 hover:bg-green-500 text-white py-2 px-4 flex items-center gap-2"
                                                            data-testid={`approve-${member.id}`}
                                                        >
                                                            <Check className="w-4 h-4" /> Approve
                                                        </button>
                                                        <button 
                                                            onClick={() => rejectOrgMember(member.id)} 
                                                            className="bg-red-600 hover:bg-red-500 text-white py-2 px-4 flex items-center gap-2"
                                                            data-testid={`reject-${member.id}`}
                                                        >
                                                            <X className="w-4 h-4" /> Reject
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Approved Members */}
                            <div className="mb-8">
                                <h4 className="font-mono text-sm text-green-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <Check className="w-4 h-4" /> Approved Members ({orgMembers.filter(m => m.status === 'approved').length})
                                </h4>
                                {orgMembers.filter(m => m.status === 'approved').length === 0 ? (
                                    <div className="text-center py-8 tactical-panel">
                                        <UserPlus className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                        <p className="font-mono text-gray-500">No approved members yet</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="data-table">
                                            <thead>
                                                <tr><th>Callsign</th><th>Email</th><th>Discord</th><th>Rank</th><th>Linked Admin</th><th>Join Date</th><th>Actions</th></tr>
                                            </thead>
                                            <tbody>
                                                {orgMembers.filter(m => m.status === 'approved').map((member) => (
                                                    <tr key={member.id}>
                                                        <td className="font-heading font-bold text-white">{member.callsign}</td>
                                                        <td className="text-gray-400">{member.email}</td>
                                                        <td className="text-gray-400">{member.discord_id}</td>
                                                        <td data-testid={`org-member-rank-${member.id}`}>{member.rank}</td>
                                                        <td>
                                                            <select
                                                                value={member.linked_admin_id || ''}
                                                                onChange={(e) => linkOrgMemberAdmin(member.id, e.target.value)}
                                                                className="bg-black/50 border border-white/10 text-white font-mono text-xs px-2 py-1 focus:outline-none focus:border-red-500/50"
                                                                data-testid={`org-member-link-admin-${member.id}`}
                                                            >
                                                                <option value="">Unlinked</option>
                                                                {admins.map(a => <option key={a.id} value={a.id}>{a.username}</option>)}
                                                            </select>
                                                        </td>
                                                        <td className="text-gray-500">{member.join_date || 'N/A'}</td>
                                                        <td>
                                                            <button onClick={() => deleteOrgMember(member.id)} className="text-red-500 hover:text-red-400">
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            
                            {/* Rejected Members */}
                            {orgMembers.filter(m => m.status === 'rejected').length > 0 && (
                                <div>
                                    <h4 className="font-mono text-sm text-red-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                                        <X className="w-4 h-4" /> Rejected ({orgMembers.filter(m => m.status === 'rejected').length})
                                    </h4>
                                    <div className="space-y-2">
                                        {orgMembers.filter(m => m.status === 'rejected').map((member) => (
                                            <div key={member.id} className="tactical-panel p-3 flex items-center justify-between opacity-60">
                                                <div className="flex items-center gap-4">
                                                    <span className="font-heading text-white">{member.callsign}</span>
                                                    <span className="text-gray-500 text-sm">{member.email}</span>
                                                </div>
                                                <button onClick={() => deleteOrgMember(member.id)} className="text-red-500 hover:text-red-400">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Members Tab */}
                    {activeTab === 'members' && (
                        <div data-testid="members-tab-content">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-heading font-bold text-lg text-white">Personnel Roster</h3>
                                <button onClick={() => openAddModal('member')} className="btn-primary py-2 px-4 flex items-center gap-2">
                                    <Plus className="w-4 h-4" /> Add Member
                                </button>
                            </div>
                            {members.length === 0 ? (
                                <div className="text-center py-12">
                                    <Users className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                    <p className="font-mono text-gray-500">No members yet</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="data-table">
                                        <thead>
                                            <tr><th>Callsign</th><th>Rank</th><th>Division</th><th>Role</th><th>Status</th><th>Actions</th></tr>
                                        </thead>
                                        <tbody>
                                            {members.map((member, idx) => (
                                                <tr key={member.id} data-testid={`admin-member-row-${member.id}`}>
                                                    <td className="font-heading font-bold text-white">{member.callsign}</td>
                                                    <td>{member.rank}</td>
                                                    <td>{member.division}</td>
                                                    <td>{member.role}</td>
                                                    <td><span className={`badge ${member.status === 'active' ? 'badge-active' : 'badge-pending'}`}>{member.status}</span></td>
                                                    <td className="flex gap-2">
                                                        <button onClick={() => openEditModal('member', member)} className="text-blue-500 hover:text-blue-400"><Edit className="w-4 h-4" /></button>
                                                        <button onClick={() => deleteItem('members', member.id)} className="text-red-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                                        <button onClick={() => moveMember(idx, -1)} disabled={idx === 0} className="text-gray-400 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed" data-testid={`member-move-up-${member.id}`}><ChevronUp className="w-4 h-4" /></button>
                                                        <button onClick={() => moveMember(idx, 1)} disabled={idx === members.length - 1} className="text-gray-400 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed" data-testid={`member-move-down-${member.id}`}><ChevronDown className="w-4 h-4" /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Ranks Tab */}
                    {activeTab === 'ranks' && (
                        <div data-testid="ranks-tab-content">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-heading font-bold text-lg text-white">Rank Hierarchy</h3>
                                <button onClick={() => openAddModal('rank')} className="btn-primary py-2 px-4 flex items-center gap-2">
                                    <Plus className="w-4 h-4" /> Add Rank
                                </button>
                            </div>
                            {ranks.length === 0 ? (
                                <div className="text-center py-12">
                                    <Award className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                    <p className="font-mono text-gray-500">No ranks defined</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {ranks.map((rank, index) => (
                                        <div key={rank.id} className="tactical-panel p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-8 h-8 flex items-center justify-center bg-black/50 border border-white/10 font-mono text-sm text-gray-400">
                                                        {rank.hierarchy}
                                                    </div>
                                                    <div 
                                                        className="w-4 h-4 rounded-full border border-white/20"
                                                        style={{ backgroundColor: rank.color }}
                                                    />
                                                    <div>
                                                        <h3 className="font-heading font-bold text-lg" style={{ color: rank.color }}>
                                                            {rank.name}
                                                        </h3>
                                                        <span className="font-mono text-xs text-gray-500">
                                                            {members.filter(m => m.rank === rank.name).length} members
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => openEditModal('rank', rank)} className="text-blue-500 hover:text-blue-400">
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    {!rank.id.startsWith('default-') && (
                                                        <button onClick={() => deleteItem('ranks', rank.id)} className="text-red-500 hover:text-red-400">
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="mt-6 tactical-panel p-4">
                                <p className="font-mono text-xs text-gray-400">
                                    <strong className="text-red-500">Tip:</strong> Lower hierarchy numbers indicate higher ranks. 
                                    Rank 1 is the highest (e.g., Admiral), Rank 5+ are lower ranks (e.g., Ensign).
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Vessels Tab */}
                    {activeTab === 'vessels' && (
                        <div data-testid="vessels-tab-content">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-heading font-bold text-lg text-white">Fleet Registry</h3>
                                <button onClick={() => openAddModal('vessel')} className="btn-primary py-2 px-4 flex items-center gap-2">
                                    <Plus className="w-4 h-4" /> Add Vessel
                                </button>
                            </div>
                            {pendingVessels.length > 0 && (
                                <div className="mb-8" data-testid="pending-vessels-section">
                                    <h4 className="font-mono text-sm text-amber-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                                        <Lock className="w-4 h-4" /> Member Submissions Awaiting Approval ({pendingVessels.length})
                                    </h4>
                                    <div className="space-y-2">
                                        {pendingVessels.map((vessel) => (
                                            <div key={vessel.id} className="tactical-panel p-4 flex items-center justify-between border-l-4 border-amber-500/50" data-testid={`pending-vessel-${vessel.id}`}>
                                                <div>
                                                    <div className="font-heading font-bold text-white">{vessel.name} <span className="font-mono text-xs text-gray-500 ml-2">{vessel.ship_class}</span></div>
                                                    <div className="text-sm text-gray-400">Submitted by <span className="text-green-400">{vessel.owner_callsign}</span>{vessel.manufacturer && ` • ${vessel.manufacturer}`}{vessel.role && ` • ${vessel.role}`}{vessel.crew_size && ` • Crew ${vessel.crew_size}`}</div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button onClick={() => setVesselApproval(vessel.id, 'approved')} className="bg-green-600 hover:bg-green-500 text-white py-2 px-3" data-testid={`approve-vessel-${vessel.id}`}><Check className="w-4 h-4" /></button>
                                                    <button onClick={() => setVesselApproval(vessel.id, 'rejected')} className="bg-red-600 hover:bg-red-500 text-white py-2 px-3" data-testid={`reject-vessel-${vessel.id}`}><X className="w-4 h-4" /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {registryVessels.length === 0 ? (
                                <div className="text-center py-12">
                                    <Ship className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                    <p className="font-mono text-gray-500">No vessels registered</p>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {registryVessels.map((vessel) => (
                                        <div key={vessel.id} className={`tactical-panel p-4 ${vessel.visibility === 'private' ? 'border-l-4 border-purple-500/50' : ''}`}>
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-mono text-xs text-gray-500">{vessel.ship_class}</span>
                                                        {vessel.visibility === 'private' && (
                                                            <span className="badge bg-purple-500/20 text-purple-400 border-purple-500/30 text-[10px] flex items-center gap-1">
                                                                <Lock className="w-3 h-3" /> Classified
                                                            </span>
                                                        )}
                                                    </div>
                                                    <h3 className="font-heading font-bold text-white">{vessel.name}</h3>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button onClick={() => openEditModal('vessel', vessel)} className="text-blue-500 hover:text-blue-400"><Edit className="w-4 h-4" /></button>
                                                    <button onClick={() => deleteItem('vessels', vessel.id)} className="text-red-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                                </div>
                                            </div>
                                            <div className="text-sm text-gray-400">
                                                <p>{vessel.role} • {vessel.manufacturer}</p>
                                                {vessel.squadron && <p className="text-red-500">Squadron: {vessel.squadron}</p>}
                                                {vessel.owner_callsign && <p className="text-green-400 text-xs font-mono mt-1">Owner: {vessel.owner_callsign}</p>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Ship Classes Tab */}
                    {activeTab === 'shipclasses' && (
                        <div data-testid="shipclasses-tab-content">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-heading font-bold text-lg text-white">Ship Class Registry</h3>
                                <button onClick={() => openAddModal('shipclass')} className="btn-primary py-2 px-4 flex items-center gap-2">
                                    <Plus className="w-4 h-4" /> Add Ship Class
                                </button>
                            </div>
                            {shipClasses.length === 0 ? (
                                <div className="text-center py-12">
                                    <Layers className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                    <p className="font-mono text-gray-500">No ship classes defined</p>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {shipClasses.map((sc) => (
                                        <div key={sc.id} className="tactical-panel p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-heading font-bold text-white">{sc.name}</h3>
                                                        <span className={`badge text-[10px] ${sc.vessel_type === 'command' ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30'}`}>
                                                            {sc.vessel_type === 'command' ? 'Command' : 'Fleet'}
                                                        </span>
                                                    </div>
                                                    {sc.description && (
                                                        <p className="text-sm text-gray-400 mt-1">{sc.description}</p>
                                                    )}
                                                </div>
                                                <div className="flex gap-2">
                                                    <button onClick={() => openEditModal('shipclass', sc)} className="text-blue-500 hover:text-blue-400"><Edit className="w-4 h-4" /></button>
                                                    {!sc.id.startsWith('default-') && (
                                                        <button onClick={() => deleteItem('ship-classes', sc.id)} className="text-red-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-xs text-gray-500 font-mono mt-2">
                                                {vessels.filter(v => v.ship_class === sc.name).length} vessels
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Squadrons Tab */}
                    {activeTab === 'squadrons' && (
                        <div data-testid="squadrons-tab-content">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-heading font-bold text-lg text-white">Squadron Registry</h3>
                                <button onClick={() => openAddModal('squadron')} className="btn-primary py-2 px-4 flex items-center gap-2">
                                    <Plus className="w-4 h-4" /> Add Squadron
                                </button>
                            </div>
                            {squadrons.length === 0 ? (
                                <div className="text-center py-12">
                                    <Crosshair className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                    <p className="font-mono text-gray-500">No squadrons registered</p>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 gap-4">
                                    {squadrons.map((squadron) => (
                                        <div key={squadron.id} className="tactical-panel p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <span className="font-mono text-xs text-red-500">{squadron.designation}</span>
                                                    <h3 className="font-heading font-bold text-white">{squadron.name}</h3>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button onClick={() => openEditModal('squadron', squadron)} className="text-blue-500 hover:text-blue-400"><Edit className="w-4 h-4" /></button>
                                                    <button onClick={() => deleteItem('squadrons', squadron.id)} className="text-red-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                                </div>
                                            </div>
                                            <p className="text-sm text-gray-400">{squadron.role}</p>
                                            <p className="text-sm text-gray-500 mt-1">Commander: {squadron.commander} • {squadron.ship_count} ships</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Operations Tab */}
                    {activeTab === 'operations' && (
                        <div data-testid="operations-tab-content">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-heading font-bold text-lg text-white">Operations</h3>
                                <button onClick={() => openAddModal('operation')} className="btn-primary py-2 px-4 flex items-center gap-2">
                                    <Plus className="w-4 h-4" /> Add Operation
                                </button>
                            </div>
                            {operations.length === 0 ? (
                                <div className="text-center py-12">
                                    <Target className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                    <p className="font-mono text-gray-500">No operations</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {operations.map((op) => (
                                        <div key={op.id} className="tactical-panel p-4">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <span className="font-mono text-xs text-gray-500">{op.code_name}</span>
                                                    <h3 className="font-heading font-bold text-white">{op.name}</h3>
                                                    <p className="text-sm text-gray-400">{op.objective}</p>
                                                    <p className="text-xs text-gray-500 mt-1">System: {op.system} • {op.start_date}</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className={`badge ${op.status === 'active' ? 'badge-active' : op.status === 'planned' ? 'badge-pending' : 'badge-completed'}`}>
                                                        {op.status}
                                                    </span>
                                                    <button onClick={() => openEditModal('operation', op)} className="text-blue-500 hover:text-blue-400"><Edit className="w-4 h-4" /></button>
                                                    <button onClick={() => deleteItem('operations', op.id)} className="text-red-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Announcements Tab */}
                    {activeTab === 'announcements' && (
                        <div data-testid="announcements-tab-content">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-heading font-bold text-lg text-white">Announcements</h3>
                                <button onClick={() => openAddModal('announcement')} className="btn-primary py-2 px-4 flex items-center gap-2">
                                    <Plus className="w-4 h-4" /> Add Announcement
                                </button>
                            </div>
                            {announcements.length === 0 ? (
                                <div className="text-center py-12">
                                    <Megaphone className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                    <p className="font-mono text-gray-500">No announcements</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {announcements.map((ann) => (
                                        <div key={ann.id} className="tactical-panel p-4">
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h3 className="font-heading font-bold text-white">{ann.title}</h3>
                                                        <span className={`badge ${ann.priority === 'urgent' ? 'badge-urgent' : ann.priority === 'high' ? 'badge-pending' : 'badge-active'}`}>
                                                            {ann.priority}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-gray-400">{ann.content}</p>
                                                    <span className="font-mono text-xs text-gray-500 mt-2 block">{ann.author}</span>
                                                </div>
                                                <div className="flex gap-2 ml-4">
                                                    <button onClick={() => openEditModal('announcement', ann)} className="text-blue-500 hover:text-blue-400"><Edit className="w-4 h-4" /></button>
                                                    <button onClick={() => deleteItem('announcements', ann.id)} className="text-red-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Comms Relay (Ticker) Tab */}
                    {activeTab === 'ticker' && (
                        <div data-testid="ticker-tab-content">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h3 className="font-heading font-bold text-lg text-white">Comms Relay Messages</h3>
                                    <p className="font-mono text-xs text-gray-500 mt-1">Scrolling messages displayed on The Bridge</p>
                                </div>
                                <button onClick={() => openAddModal('ticker')} className="btn-primary py-2 px-4 flex items-center gap-2">
                                    <Plus className="w-4 h-4" /> Add Message
                                </button>
                            </div>
                            
                            {/* Speed Control */}
                            <div className="tactical-panel p-4 mb-6">
                                <div className="flex flex-col md:flex-row md:items-center gap-4">
                                    <div className="flex items-center gap-3">
                                        <Radio className="w-5 h-5 text-red-500" />
                                        <span className="font-mono text-sm text-white uppercase tracking-wider">Scroll Speed</span>
                                    </div>
                                    <div className="flex-1 flex items-center gap-4">
                                        <span className="font-mono text-xs text-gray-500">Fast</span>
                                        <input 
                                            type="range" 
                                            min="10" 
                                            max="60" 
                                            value={tickerSpeed}
                                            onChange={(e) => handleUpdateTickerSpeed(parseInt(e.target.value))}
                                            className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-500"
                                            data-testid="ticker-speed-slider"
                                        />
                                        <span className="font-mono text-xs text-gray-500">Slow</span>
                                    </div>
                                    <div className="font-mono text-sm text-red-500 min-w-[80px] text-right">
                                        {tickerSpeed}s cycle
                                    </div>
                                </div>
                            </div>

                            {tickerMessages.length === 0 ? (
                                <div className="text-center py-12">
                                    <Radio className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                    <p className="font-mono text-gray-500">No ticker messages</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {tickerMessages.map((msg) => (
                                        <div key={msg.id} className={`tactical-panel p-4 ${!msg.active ? 'opacity-50' : ''}`}>
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Radio className={`w-4 h-4 ${msg.active ? 'text-green-500' : 'text-gray-500'}`} />
                                                        <span className={`badge ${msg.active ? 'badge-active' : 'badge-pending'}`}>
                                                            {msg.active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                    <p className="font-mono text-sm text-gray-300">{msg.message}</p>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button onClick={() => openEditModal('ticker', msg)} className="text-blue-500 hover:text-blue-400"><Edit className="w-4 h-4" /></button>
                                                    {!msg.id.startsWith('default-') && (
                                                        <button onClick={() => deleteItem('ticker-messages', msg.id)} className="text-red-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Admins Tab */}
                    {activeTab === 'admins' && (
                        <div data-testid="admins-tab-content">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-heading font-bold text-lg text-white">Admin Credentials</h3>
                                <button onClick={() => openAddModal('admin')} className="btn-primary py-2 px-4 flex items-center gap-2">
                                    <Plus className="w-4 h-4" /> Create Admin
                                </button>
                            </div>
                            {admins.length === 0 ? (
                                <div className="text-center py-12">
                                    <Key className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                                    <p className="font-mono text-gray-500">No admins found</p>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {admins.map((adm) => {
                                        const isFounderAccount = adm.username === 'AceShepherd';
                                        const isCurrentUser = adm.id === admin.id;
                                        const canDelete = !isFounderAccount && !isCurrentUser;
                                        
                                        return (
                                            <div key={adm.id} className={`tactical-panel p-4 ${isCurrentUser ? 'border-green-500/50' : ''}`}>
                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 ${isFounderAccount ? 'bg-amber-500/20 border-amber-500/30' : isCurrentUser ? 'bg-green-500/20 border-green-500/30' : 'bg-red-500/20 border-red-500/30'} border flex items-center justify-center`}>
                                                            <Key className={`w-5 h-5 ${isFounderAccount ? 'text-amber-500' : isCurrentUser ? 'text-green-500' : 'text-red-500'}`} />
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <h3 className="font-heading font-bold text-white">{adm.username}</h3>
                                                                {isCurrentUser && (
                                                                    <span className="bg-green-500/20 border border-green-500/30 text-green-500 text-[10px] font-mono px-2 py-0.5 uppercase">
                                                                        You
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span className={`font-mono text-xs ${isFounderAccount ? 'text-amber-500' : 'text-gray-500'}`}>
                                                                {isFounderAccount ? 'Admin (Founder)' : 'Admin'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {canDelete && (
                                                        <button 
                                                            onClick={() => deleteItem('admins', adm.id)} 
                                                            className="text-red-500 hover:text-red-400"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Backgrounds Tab (Founder Only) */}
                    {activeTab === 'backgrounds' && isFounder && (
                        <div data-testid="backgrounds-tab-content">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h3 className="font-heading font-bold text-lg text-white">Page Backgrounds</h3>
                                    <p className="font-mono text-xs text-amber-500 mt-1">Founder-only: Customize page hero images</p>
                                </div>
                                <button onClick={handleUpdateBackgrounds} className="btn-primary py-2 px-6 flex items-center gap-2">
                                    <Save className="w-4 h-4" /> Save All Backgrounds
                                </button>
                            </div>
                            
                            <div className="grid gap-6">
                                <h4 className="font-mono text-sm text-white uppercase tracking-wider border-b border-white/10 pb-2">Page Hero Images</h4>
                                {[
                                    { key: 'bridge', label: 'The Bridge (Home)', default: 'https://static.prod-images.emergentagent.com/jobs/9645c0b6-481f-4d07-81fb-c2ea43b51b14/images/b3e5202161cf537fb0cf7fbf86c118d612f12ee77c72cd41f3a1d665347845d6.png' },
                                    { key: 'hangar', label: 'Hangar', default: 'https://static.prod-images.emergentagent.com/jobs/9645c0b6-481f-4d07-81fb-c2ea43b51b14/images/d30338b369eaa06ed2484a5aab57e44883e9e43ba0bbe8f54ecf63abb4ce73fd.png' },
                                    { key: 'war_room', label: 'War Room', default: 'https://static.prod-images.emergentagent.com/jobs/9645c0b6-481f-4d07-81fb-c2ea43b51b14/images/6e474b3df22e3055f6943190a41366b29565a89e25194d1b6c4b81cc9373a511.png' },
                                    { key: 'personnel', label: 'Personnel', default: 'https://static0.polygonimages.com/wordpress/wp-content/uploads/chorus/uploads/chorus_asset/file/13719978/Screenshot_2019_01_28_12.14.51.png?w=1600&h=900&fit=crop' },
                                    { key: 'operations', label: 'Operations', default: 'https://images.unsplash.com/photo-1762441112136-4dfc6edf58e8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85' },
                                    { key: 'announcements', label: 'Announcements', default: 'https://images.unsplash.com/photo-1760813360432-9b5d79eb9679?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85' },
                                ].map((page) => (
                                    <div key={page.key} className="tactical-panel p-4">
                                        <div className="flex flex-col lg:flex-row gap-4">
                                            <div className="flex-1">
                                                <label className="form-label">{page.label}</label>
                                                <input 
                                                    className="form-input" 
                                                    placeholder="Enter image URL..."
                                                    value={backgrounds[page.key] || ''} 
                                                    onChange={e => setBackgrounds({...backgrounds, [page.key]: e.target.value})}
                                                />
                                                <p className="font-mono text-[10px] text-gray-600 mt-1">
                                                    Default: {page.default ? 'Set' : 'None'}
                                                </p>
                                            </div>
                                            {(backgrounds[page.key] || page.default) && (
                                                <div className="w-32 h-20 bg-black/50 border border-white/10 overflow-hidden flex-shrink-0">
                                                    <img 
                                                        src={backgrounds[page.key] || page.default} 
                                                        alt={page.label}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => e.target.style.display = 'none'}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                
                                <h4 className="font-mono text-sm text-white uppercase tracking-wider border-b border-white/10 pb-2 mt-6">Mission Profile Images</h4>
                                {[
                                    { key: 'mission_strike', label: 'Strike Mission', default: 'https://static.prod-images.emergentagent.com/jobs/9645c0b6-481f-4d07-81fb-c2ea43b51b14/images/e95c7ff7ad6bd161cbb58b55c1f26e2c2be399dbba3c28a4bbde6ab6465bfebe.png' },
                                    { key: 'mission_escort', label: 'Escort Mission', default: '' },
                                    { key: 'mission_recon', label: 'Recon Mission', default: '' },
                                ].map((page) => (
                                    <div key={page.key} className="tactical-panel p-4">
                                        <div className="flex flex-col lg:flex-row gap-4">
                                            <div className="flex-1">
                                                <label className="form-label">{page.label}</label>
                                                <input 
                                                    className="form-input" 
                                                    placeholder="Enter image URL..."
                                                    value={backgrounds[page.key] || ''} 
                                                    onChange={e => setBackgrounds({...backgrounds, [page.key]: e.target.value})}
                                                />
                                                <p className="font-mono text-[10px] text-gray-600 mt-1">
                                                    Default: {page.default ? 'Set' : 'None'}
                                                </p>
                                            </div>
                                            {(backgrounds[page.key] || page.default) && (
                                                <div className="w-32 h-20 bg-black/50 border border-white/10 overflow-hidden flex-shrink-0">
                                                    <img 
                                                        src={backgrounds[page.key] || page.default} 
                                                        alt={page.label}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => e.target.style.display = 'none'}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="mt-6 tactical-panel p-4">
                                <p className="font-mono text-xs text-gray-400">
                                    <strong className="text-amber-500">Tip:</strong> Use direct image URLs (ending in .jpg, .png, .webp). 
                                    Images should be at least 1920x1080 for best quality. Leave empty to use default images.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Add Modal */}
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl uppercase">
                            Add {modalType}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                        {renderFormFields()}
                        <div className="flex gap-3 pt-4">
                            <button onClick={() => setShowAddModal(false)} className="btn-secondary flex-1">Cancel</button>
                            <button onClick={handleSubmitAdd} className="btn-primary flex-1">Add {modalType}</button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Modal */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl uppercase">
                            Edit {modalType}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                        {renderFormFields()}
                        <div className="flex gap-3 pt-4">
                            <button onClick={() => setShowEditModal(false)} className="btn-secondary flex-1">Cancel</button>
                            <button onClick={handleSubmitEdit} className="btn-primary flex-1">Save Changes</button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};
